import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { useFeatureFlags } from 'features/project/hooks/use-feature-flags'
import type { FeatureFlagsResponse } from 'features/project/service/feature-flags-service'

/**
 * Gate a page on a feature flag, redirecting away only once the answer is known.
 *
 * Exists because the hand-rolled version of this races. Written inline the
 * obvious way —
 *
 *     const hasFlag = !!flags?.[KEY]
 *     useEffect(() => { if (!hasFlag) navigate(fallback) }, [hasFlag])
 *
 * — the effect fires on the FIRST render, when the flags request has not
 * resolved and `data` is still `undefined`. `hasFlag` is therefore false for a
 * user who does have the flag, and they are bounced off the page. It reproduces
 * on any hard refresh or deep link, because react-query has no cached answer
 * yet; it only looks fine when navigating in-app from a page that already
 * warmed the cache.
 *
 * The fix is to redirect on `isSuccess && !enabled` rather than on `!enabled`:
 * act on a resolved "off", never on "not yet known".
 *
 * A flags request that ERRORS deliberately does not redirect. We cannot tell
 * whether the flag is on, and bouncing on a transient network failure is the
 * same user-visible bug this hook exists to remove. There is no security delta
 * in staying: the gate is presentational, and the API enforces no flag of its
 * own (see the insight_agent feature's AGENTS.md).
 */
export function useFeatureFlagGate(
  flag: string,
  fallbackPath: string,
): boolean {
  // Destructured as `flagsResponse`, not `data` — `no-generic-identifier-data`
  // bans the bare name, which is why every existing call site renames it too.
  const { data: flagsResponse, isSuccess } = useFeatureFlags()
  const navigate = useNavigate()

  // A non-global flag with no override for this project/user is ABSENT from the
  // response rather than `false`, so coerce rather than compare.
  const enabled = !!(flagsResponse as FeatureFlagsResponse | undefined)?.[flag]

  useEffect(() => {
    if (isSuccess && !enabled) navigate(fallbackPath)
  }, [isSuccess, enabled, navigate, fallbackPath])

  return enabled
}
